import axios, { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { useOrderStore } from '#src/store/order';
import { api } from 'services/api';
import Partner from '@models/Partner';

type ApiPartnerError = {
  error: string;
  key: string;
};

export const usePartnerStore = defineStore({
  id: 'partner',
  state: () => ({
    generatingCode: false,
    partners: {} as { [key: string]: Partner },
    inProgress: {} as { [key: string]: Promise<AxiosResponse> },
  }),
  actions: {
    async getPartner(key: string) {
      if (key === '') {
        return null;
      }

      const existingPartner = this.partners[key];

      if (existingPartner !== undefined) {
        return existingPartner;
      }

      if (Boolean(this.inProgress[key])) {
        return this.inProgress[key];
      }

      try {
        const promise = api.get<ApiPartner>(`/partner/${key}`);
        this.inProgress[key] = promise;
        const response = await promise;
        this.insertPartner(response.data);
      } catch (error) {
        this.insertPartner({ error: (<Error>error).message, key });
      }

      delete this.inProgress[key];
    },
    async generateActivationCode(payload: CreateActivationCodePayload) {
      const orderStore = useOrderStore();

      this.generatingCode = true;

      let response = null;
      try {
        response = await api.post<ApiActivationCode>('/partner/activation-code', payload);
        // TODO: handle 15 minute delay (if refresh occurs)
        orderStore.updateOrder({ orderNumber: payload.orderNumber, orderData: { code: response.data.code } });
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          console.error(
            'generateActivationCode error',
            `${<string>(<unknown>error.response.status)}`,
            error.response.data
          );
        } else {
          console.error('generateActivationCode error:', error);
        }
      }

      this.generatingCode = false;

      return response?.data?.code;
    },
    insertPartner(partnerData: ApiPartner | ApiPartnerError) {
      let partner;

      if ((<ApiPartner>partnerData).id) {
        const apiPartner = <ApiPartner>partnerData;
        partner = {
          id: apiPartner.id,
          key: apiPartner.key,
          name: apiPartner.name,
        };
      } else {
        partner = partnerData;
      }

      this.partners[partner.key] = <Partner>partner;
    },
  },
});
